import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Card, Stepper } from '@percent/hosted-validation-form/ui';
import { useValidationStepData } from '../../validationStepContext/useValidationStepData/useValidationStepData';
import { PRIVACY_POLICY_URL } from '@percent/hosted-validation-form/constants/privacyPolicyUrl';
import { TERMS_URL } from '@percent/hosted-validation-form/constants/termsUrl';
import { ReactComponent as Expand } from '@percent/hosted-validation-form/assets/icons/expand.svg';
import { ErrorCard } from '@percent/hosted-validation-form/ui/ErrorCard/ErrorCard';
import { isInvalidCaptchaTokenError } from '@percent/hosted-validation-form/utils/isInvalidCaptchaTokenError';
import { ErrorVariant } from '@percent/domain/verifications';
import { Flex, Loader, Typography, Checkbox } from '@percent/lemonade';
import { useProgress } from '@percent/hosted-validation-form/hooks';

import { TermsProps } from './Terms.types';
import { Link } from 'react-router-dom';
import styles from './Terms.module.scss';

export const Terms = ({ handleFormSubmit, isLoading, isError, error }: TermsProps) => {
  const { handleBack } = useValidationStepData();
  const [isChecked, setIsChecked] = useState(false);
  const { t } = useTranslation();
  const { progress } = useProgress();

  const label = (
    <Typography className={styles.label} variant="default-medium">
      <Trans t={t} i18nKey="hostedValidation.terms.agreeTerms">
        I agree to the{' '}
        <Link className={styles.link} target="_blank" to={TERMS_URL} rel="noopener noreferrer">
          terms & conditions <Expand />
        </Link>
        and{' '}
        <Link className={styles.link} target="_blank" to={PRIVACY_POLICY_URL} rel="noopener noreferrer">
          privacy policy <Expand />
        </Link>
      </Trans>
    </Typography>
  );

  if (isLoading) {
    return (
      <Card>
        <Loader />
      </Card>
    );
  }

  if (isError) {
    // An unanswered challenge is the user's to retry, not a fault to apologise for.
    const title = isInvalidCaptchaTokenError(error)
      ? t('hostedValidation.errorMessage.captchaIncomplete', {
          defaultValue: 'Please complete the challenge and try again',
        })
      : t('hostedValidation.errorMessage.somethingWentWrong');

    return (
      <ErrorCard
        title={title}
        subtitle={t('hostedValidation.errorMessage.reloadPage')}
        variant={ErrorVariant.INTERNAL}
      />
    );
  }

  return (
    <Card
      onBack={handleBack}
      title={t('hostedValidation.title.almostFinished')}
      description={t('hostedValidation.title.firstConfirm')}
      onNext={handleFormSubmit}
      nextButtonLabel={t('hostedValidation.button.next')}
      nextButtonDisabled={!isChecked}
      progress={progress}
    >
      <Flex direction="column" gap={16} w="368px">
        <Stepper />
        <Checkbox
          variant="default"
          active={isChecked}
          name="agreement"
          label={label}
          onChange={(event) => {
            const isChecked = (event as React.ChangeEvent<HTMLInputElement>).target.checked;
            setIsChecked(isChecked);
          }}
          disabled={false}
        />
      </Flex>
    </Card>
  );
};
